import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Moon, CloudRain, Cat, Waves, Play, Square } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
interface DeepSleepProps {
  onClose: () => void;
}
type OverlaySound = 'none' | 'brown' | 'rain' | 'cat';
export function DeepSleep({
  onClose
}: DeepSleepProps) {
  const [isActive, setIsActive] = useState(false);
  const [overlay, setOverlay] = useState<OverlaySound>('none');
  const [duration, setDuration] = useState(30); // minutes
  const [timeLeft, setTimeLeft] = useState(30 * 60); // seconds
  const [showUI, setShowUI] = useState(true);
  const {
    playClick
  } = useClickSound();
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscLeftRef = useRef<OscillatorNode | null>(null);
  const oscRightRef = useRef<OscillatorNode | null>(null);
  const noiseNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const uiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cleanup audio
  useEffect(() => {
    return () => stopAudio();
  }, []);
  // Timer logic
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (isActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleStop();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isActive, timeLeft]);
  // Auto-hide UI when active
  useEffect(() => {
    if (isActive) {
      resetUITimer();
    } else {
      setShowUI(true);
      if (uiTimerRef.current) clearTimeout(uiTimerRef.current);
    }
  }, [isActive]);
  const resetUITimer = () => {
    if (!isActive) return;
    setShowUI(true);
    if (uiTimerRef.current) clearTimeout(uiTimerRef.current);
    uiTimerRef.current = setTimeout(() => {
      setShowUI(false);
    }, 4000);
  };
  // Generate Brown Noise buffer
  const createBrownNoise = (ctx: AudioContext) => {
    const bufferSize = 2 * ctx.sampleRate;
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = noiseBuffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      output[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = output[i];
      output[i] *= 3.5; // Compensate gain
    }
    return noiseBuffer;
  };
  const startAudio = () => {
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0, ctx.currentTime);
      masterGain.gain.linearRampToValueAtTime(0.5, ctx.currentTime + 3);
      masterGain.connect(ctx.destination);
      gainRef.current = masterGain;
      // Binaural Beats (Delta 2Hz)
      // Left ear: 100Hz, Right ear: 102Hz
      const merger = ctx.createChannelMerger(2);
      const oscL = ctx.createOscillator();
      oscL.type = 'sine';
      oscL.frequency.value = 100;
      oscL.connect(merger, 0, 0); // Connect to left channel
      oscLeftRef.current = oscL;
      const oscR = ctx.createOscillator();
      oscR.type = 'sine';
      oscR.frequency.value = 102;
      oscR.connect(merger, 0, 1); // Connect to right channel
      oscRightRef.current = oscR;
      const beatGain = ctx.createGain();
      beatGain.gain.value = 0.3;
      merger.connect(beatGain);
      beatGain.connect(masterGain);
      oscL.start();
      oscR.start();
      // Overlay Sound
      if (overlay !== 'none') {
        const noiseSource = ctx.createBufferSource();
        noiseSource.buffer = createBrownNoise(ctx);
        noiseSource.loop = true;
        const filter = ctx.createBiquadFilter();
        const noiseGain = ctx.createGain();
        if (overlay === 'brown') {
          filter.type = 'lowpass';
          filter.frequency.value = 400;
          noiseGain.gain.value = 0.8;
        } else if (overlay === 'rain') {
          filter.type = 'bandpass';
          filter.frequency.value = 1000;
          filter.Q.value = 0.5;
          noiseGain.gain.value = 0.5;
        } else if (overlay === 'cat') {
          filter.type = 'lowpass';
          filter.frequency.value = 150;
          // Add tremolo for purring effect
          const lfo = ctx.createOscillator();
          lfo.type = 'sine';
          lfo.frequency.value = 25; // 25Hz purr
          const lfoGain = ctx.createGain();
          lfoGain.gain.value = 0.5;
          lfo.connect(lfoGain);
          lfoGain.connect(noiseGain.gain);
          lfo.start();
          noiseGain.gain.value = 0.6;
        }
        noiseSource.connect(filter);
        filter.connect(noiseGain);
        noiseGain.connect(masterGain);
        noiseSource.start();
        noiseNodeRef.current = noiseSource;
      }
    } catch (e) {
      console.error('Audio setup failed', e);
    }
  };
  const stopAudio = () => {
    if (gainRef.current && audioCtxRef.current) {
      const ctx = audioCtxRef.current;
      gainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 2);
      setTimeout(() => {
        try {
          oscLeftRef.current?.stop();
        } catch (e) {}
        try {
          oscRightRef.current?.stop();
        } catch (e) {}
        try {
          noiseNodeRef.current?.stop();
        } catch (e) {}
        if (ctx.state !== 'closed') ctx.close();
      }, 2000);
    }
  };
  const handleStart = () => {
    playClick();
    setTimeLeft(duration * 60);
    setIsActive(true);
    startAudio();
  };
  const handleStop = () => {
    playClick();
    setIsActive(false);
    stopAudio();
  };
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col w-full h-full overflow-hidden bg-black text-white" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} onPointerMove={resetUITimer} onTouchStart={resetUITimer} data-id="element-431">
      {/* Subtle background wave animation when active */}
      <AnimatePresence data-id="element-432">
        {isActive && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-indigo-900/20 to-transparent pointer-events-none" data-id="element-433">
            <motion.div className="absolute bottom-0 left-0 w-full h-full opacity-30" animate={{
          backgroundPosition: ['0% 100%', '100% 100%']
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} style={{
          backgroundImage: 'radial-gradient(ellipse at center, rgba(79, 70, 229, 0.15) 0%, transparent 70%)',
          backgroundSize: '200% 100%'
        }} data-id="element-434" />
          </motion.div>}
      </AnimatePresence>

      {/* UI Layer */}
      <motion.div className="flex flex-col w-full h-full relative z-10" animate={{
      opacity: showUI ? 1 : 0.1
    }} transition={{
      duration: 1
    }} data-id="element-435">
        {/* Header */}
        <div className="w-full flex items-center justify-between p-4 shrink-0" data-id="element-436">
          <button onClick={() => {
          playClick();
          onClose();
        }} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:bg-white/10 transition-colors border border-white/10" data-id="element-437">
            <ArrowLeft className="w-5 h-5" data-id="element-438" />
          </button>
          <div className="flex flex-col items-center" data-id="element-439">
            <span className="text-[10px] text-indigo-400 font-bold tracking-widest uppercase" data-id="element-440">
              Fale Delta (0.5 - 4 Hz)
            </span>
            <span className="text-sm font-medium text-white/90" data-id="element-441">
              Głęboki Sen
            </span>
          </div>
          <div className="w-10 h-10" data-id="element-442" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center p-6" data-id="element-443">
          <div className="w-24 h-24 rounded-full bg-indigo-950/50 flex items-center justify-center mb-8 border border-indigo-900/50 shadow-[0_0_40px_rgba(49,45,129,0.3)]" data-id="element-444">
            <Moon className="w-10 h-10 text-indigo-300" strokeWidth={1.5} data-id="element-445" />
          </div>

          <div className="text-5xl font-light mb-12 tabular-nums text-white/90 tracking-wider" data-id="element-446">
            {formatTime(timeLeft)}
          </div>

          {!isActive && <div className="w-full max-w-xs space-y-8" data-id="element-447">
              {/* Overlay Selection */}
              <div data-id="element-448">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3 text-center" data-id="element-449">
                  Dźwięk w tle
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-450">
                  <button onClick={() => {
                playClick();
                setOverlay('brown');
              }} className={`flex flex-col items-center p-3 rounded-2xl border transition-all ${overlay === 'brown' ? 'bg-indigo-900/40 border-indigo-500/50 text-indigo-200' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`} data-id="element-451">
                    <Waves className="w-6 h-6 mb-2" data-id="element-452" />
                    <span className="text-[10px] font-medium" data-id="element-453">Szum</span>
                  </button>
                  <button onClick={() => {
                playClick();
                setOverlay('rain');
              }} className={`flex flex-col items-center p-3 rounded-2xl border transition-all ${overlay === 'rain' ? 'bg-indigo-900/40 border-indigo-500/50 text-indigo-200' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`} data-id="element-454">
                    <CloudRain className="w-6 h-6 mb-2" data-id="element-455" />
                    <span className="text-[10px] font-medium" data-id="element-456">Deszcz</span>
                  </button>
                  <button onClick={() => {
                playClick();
                setOverlay('cat');
              }} className={`flex flex-col items-center p-3 rounded-2xl border transition-all ${overlay === 'cat' ? 'bg-indigo-900/40 border-indigo-500/50 text-indigo-200' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`} data-id="element-457">
                    <Cat className="w-6 h-6 mb-2" data-id="element-458" />
                    <span className="text-[10px] font-medium" data-id="element-459">Mruczenie</span>
                  </button>
                </div>
              </div>

              {/* Timer Selection */}
              <div data-id="element-460">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest mb-3 text-center" data-id="element-461">
                  Czas trwania
                </p>
                <div className="flex justify-center space-x-2" data-id="element-462">
                  {[30, 60, 120].map(t => <button key={t} onClick={() => {
                playClick();
                setDuration(t);
                setTimeLeft(t * 60);
              }} className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${duration === t ? 'bg-white text-black border-white' : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'}`} data-id="element-463">
                      {t} min
                    </button>)}
                </div>
              </div>
            </div>}
        </div>

        {/* Play/Stop Button */}
        <div className="p-8 flex justify-center pb-12" data-id="element-464">
          {!isActive ? <button onClick={handleStart} className="w-20 h-20 rounded-full bg-indigo-600 flex items-center justify-center text-white hover:bg-indigo-500 transition-colors shadow-[0_0_30px_rgba(79,70,229,0.4)]" data-id="element-465">
              <Play className="w-8 h-8 ml-1" fill="currentColor" data-id="element-466" />
            </button> : <button onClick={handleStop} className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20 transition-colors border border-white/10" data-id="element-467">
              <Square className="w-6 h-6" fill="currentColor" data-id="element-468" />
            </button>}
        </div>
      </motion.div>
    </motion.div>;
}